import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { type Client, ClientRow, clientsQueryOptions } from '@/entities/client'
import { ClientCreateDialog, ClientDialog } from '@/features/client-editor'
import { pluralRu, useDebouncedValue } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { EmptyState } from '@/shared/ui/empty-state'
import { Input } from '@/shared/ui/field'
import { PageBody } from '@/shared/ui/page-body'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'

export const Route = createFileRoute('/_app/clients')({
  component: ClientsPage,
})

function ClientsPage() {
  const [query, setQuery] = useState('')
  const [includeForgotten, setIncludeForgotten] = useState(false)
  const [open, setOpen] = useState<Client | null>(null)
  const [creating, setCreating] = useState(false)
  const debounced = useDebouncedValue(query, 300)

  const clientsQuery = useQuery(
    clientsQueryOptions({ query: debounced, includeForgotten, includeMerged: false }),
  )
  const clients = clientsQuery.data ?? []

  return (
    <PageBody>
      <PageHeader
        title="Клиенты"
        actions={
          <Button size="sm" onClick={() => setCreating(true)}>
            Добавить клиента
          </Button>
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-3">
        <Input
          type="search"
          aria-label="Поиск по имени или телефону"
          placeholder="Имя или телефон"
          className="max-w-sm flex-1"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Checkbox
          checked={includeForgotten}
          onChange={() => setIncludeForgotten(!includeForgotten)}
          label={<span className="text-sm text-text-secondary">показывать стёртых</span>}
        />
        {/* Счёт найденного: поиск с задержкой, и без числа непонятно, ответил
            ли список на набранное или ещё показывает прежнее. */}
        {!clientsQuery.isLoading && clients.length > 0 && (
          <span className="ml-auto text-xs text-text-tertiary">
            {clients.length} {pluralRu(clients.length, 'человек', 'человека', 'человек')}
          </span>
        )}
      </div>

      {clientsQuery.isLoading ? (
        <Spinner />
      ) : clients.length === 0 ? (
        <EmptyState
          title={debounced ? 'Никого не нашлось' : 'Клиентов пока нет'}
          description={
            debounced
              ? 'Проверьте написание имени или попробуйте телефон — или заведите контакт вручную.'
              : 'Клиенты появляются сами, когда записываются по ссылке. Пришедшего мимо ссылки заводите вручную.'
          }
        />
      ) : (
        <ul className="flex flex-col">
          {clients.map((client) => (
            <ClientRow key={client.id} client={client} onOpen={setOpen} />
          ))}
        </ul>
      )}

      <ClientDialog client={open} candidates={clients} onClose={() => setOpen(null)} />
      <ClientCreateDialog open={creating} prefill={debounced} onClose={() => setCreating(false)} />
    </PageBody>
  )
}
